import type { AnyRouter } from '@tanstack/react-router'

/**
 * Version-drift reload.
 *
 * When a new build deploys mid-session, the running client keeps executing the OLD bundle. Now
 * that hashed assets persist on the CDN (see apps/server/scripts/upload-to-cdn.ts), the old client
 * never hits a missing-chunk error — so it would otherwise run stale code indefinitely. To pick up
 * the new version without yanking the page out from under the user, we reload on their NEXT
 * navigation:
 *
 *  - The server stamps every dynamic response with the deployed commit hash (APP_VERSION_HEADER,
 *    see apps/server/server.bun.ts) and bakes it into the SSR document as `window.APP_VERSION`.
 *  - The client captures the latest advertised version from response headers (every tRPC/loader
 *    fetch carries it) and, on the next route resolution, full-reloads if it differs from the
 *    version this document was served with.
 *
 * A short cooldown guards against a reload loop while a rollout briefly serves two versions behind
 * the load balancer.
 */

// Wire constant — kept in sync with apps/server/server.bun.ts (no shared package between the two).
export const APP_VERSION_HEADER = 'x-danchiano-app-version'

const RELOAD_GUARD_KEY = 'danchiano:version-reload-at'
const RELOAD_COOLDOWN_MS = 30_000

let latestSeenVersion = ''

function reloadedRecentlyFor(key: string): boolean {
  try {
    const at = window.sessionStorage.getItem(key)
    return !!at && Date.now() - Number(at) < RELOAD_COOLDOWN_MS
  } catch {
    return false
  }
}

/**
 * Neutralize TanStack Router's built-in "reload on missing module" recovery for a failed chunk import.
 *
 * `lazyRouteComponent` assumes a failed dynamic import means a stale deploy (the chunk was deleted by a newer
 * build) and `window.location.reload()`s ONCE to fetch the new HTML. Here that assumption is wrong almost every
 * time: hashed assets PERSIST on the CDN (upload-to-cdn.ts), so an old client's chunks are still there and a
 * failed import is a NETWORK failure — offline, a captive portal, a flaky connection. Reloading into a dead
 * network replaces the app with a blank `chrome-error://` document; the genuine stale-deploy case is already
 * covered proactively by setupVersionDriftReload below.
 *
 * TanStack guards its reload with a sessionStorage key, `tanstack_router_reload:<error.message>`. Pre-setting
 * that key from our `vite:preloadError` listener (which fires synchronously, BEFORE lazyRouteComponent inspects
 * it) makes TanStack skip the reload, so the rejection instead surfaces to the route error boundary — which
 * waits for the server to actually be reachable before reloading, with the chrome intact. See route-error.tsx.
 */
export function suppressTanstackChunkReload(payload: unknown): void {
  const message = payload instanceof Error ? payload.message : typeof payload === 'string' ? payload : ''
  if (!message) return
  try {
    window.sessionStorage.setItem(`tanstack_router_reload:${message}`, '1')
  } catch {
    // sessionStorage unavailable (private mode / sandboxed iframe) — TanStack's own guard read is wrapped in
    // the same availability check, so it won't reload either. Nothing to do.
  }
}

/** The deployed version this running document/bundle was served as (baked SSR-side; see __root). */
function loadedVersion(): string {
  return (typeof window !== 'undefined' && window.APP_VERSION) || ''
}

/** Wrap `window.fetch` once to record the version the server advertises on every response. */
function captureVersionFromFetch() {
  const original = window.fetch
  // Object.assign preserves the static members of `fetch` (e.g. `fetch.preconnect`) on the wrapper.
  window.fetch = Object.assign((...args: Parameters<typeof fetch>) => {
    return original(...args).then((response) => {
      const version = response.headers.get(APP_VERSION_HEADER)
      if (version) latestSeenVersion = version
      return response
    })
  }, original)
}

function reloadedRecently(): boolean {
  return reloadedRecentlyFor(RELOAD_GUARD_KEY)
}

/**
 * Arm the drift check on a client router. No-op when no version is stamped (dev / e2e build),
 * so local development never triggers reloads.
 */
export function setupVersionDriftReload(router: AnyRouter) {
  if (loadedVersion() === '') return
  captureVersionFromFetch()
  router.subscribe('onResolved', () => {
    if (!latestSeenVersion || latestSeenVersion === loadedVersion()) return
    if (reloadedRecently()) return
    try {
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
    } catch {
      // sessionStorage unavailable (private mode / sandboxed iframe) — reload anyway; the
      // worst case is one extra reload, which still lands the user on the new version.
    }
    window.location.reload()
  })
}
